import { FieldType, ModalFieldConfig } from '../cms/modalConfig';
import { SHARED_CURRENCIES } from '../shared/constants';

export interface CustomerProfileFieldConfig extends Omit<ModalFieldConfig, 'type'> {
    type: FieldType;
}

// MODAL FIELD CONFIGURATION FOR CUSTOMER PROFILES   
export const CUSTOMER_PROFILE_MODAL_FIELDS: Record<string, CustomerProfileFieldConfig[]> = {
    add: [
        // { name: 'profileHeading', label: 'Profile Image (Optional)', type: 'section-heading' as FieldType },
        // { name: 'profileImage', label: 'Upload Image', type: 'image-upload-circle' as FieldType, placeholder: 'Recommended: Square image, at least 400x400px. Max file size: 5MB' },

        { name: 'personalHeading', label: 'Personal Information', type: 'section-heading' as FieldType },
        { name: 'firstName', label: 'First Name', type: 'text' as FieldType, required: true, minLength: 2, placeholder: 'Enter first name', colSpan: 'half' },
        { name: 'lastName', label: 'Last Name', type: 'text' as FieldType, required: true, minLength: 2, placeholder: 'Enter last name', colSpan: 'half' },

        { name: 'contactHeading', label: 'Contact Information', type: 'section-heading' as FieldType, placeholder: '(At least one required)' },


        { name: 'email', label: 'Email Address', type: 'email' as FieldType, placeholder: 'customer@example.com' },
        { name: 'phone', label: 'Phone Number', type: 'phone-input' as FieldType, placeholder: '50 123 4567', helperText: 'Enter phone number without country code' },
        { name: 'dob', label: 'Date of Birth', type: 'date' as FieldType, required: false },

        { name: 'securityHeading', label: 'Account Security', type: 'section-heading' as FieldType },
        {
            name: 'securityBox',
            label: '',
            type: 'static-box' as FieldType,
            placeholder: 'Password fields will appear after adding contact details',
            helperText: 'Contact info is required to create account credentials',
            showWhen: (values: any) => (!values.email || values.email === '')
        },

        {
            name: 'password',
            label: 'Password',
            type: 'password' as FieldType,
            required: true,
            placeholder: 'Enter password',
            helperText: 'Password must be at least 8 characters long',
            showWhen: (values: any) => (values.email && values.email !== '')
        },
        {
            name: 'confirmPassword',
            label: 'Confirm Password',
            type: 'password' as FieldType,
            required: true,
            placeholder: 'Re-enter password',
            showWhen: (values: any) => (values.email && values.email !== '')
        },

        { name: 'loyaltyHeading', label: 'Loyalty & Preferences', type: 'section-heading' as FieldType },
        {
            name: 'loyaltyTier', label: 'Loyalty Tier', type: 'select' as FieldType, colSpan: 'half', value: 'SELECT', options: [
                { value: 'SELECT', label: 'SELECT' },
                { value: 'SIGNATURE', label: 'SIGNATURE' },
                { value: 'ELITE', label: 'ELITE' },
                { value: 'BLACK', label: 'BLACK' },
                { value: 'PRIVÉ', label: 'PRIVÉ' }
            ]
        },
        {
            name: 'currency', label: 'Currency', type: 'select' as FieldType, colSpan: 'half', options: SHARED_CURRENCIES
        },

        { name: 'commHeading', label: 'Communication Preferences', type: 'section-heading' as FieldType },
        {
            name: 'marketingPrefs', label: 'Offers, Marketing, Surveys & Updates', type: 'checkbox-grid' as FieldType,
            value: ['push', 'email', 'whatsapp', 'sms'],
            options: [
                { value: 'push', label: 'Push' },
                { value: 'email', label: 'Email' },
                { value: 'whatsapp', label: 'WhatsApp' },
                { value: 'sms', label: 'SMS' }
            ]
        },
        {
            name: 'accountPrefs', label: 'Account Related (Bookings, Changes, Cancellations & Reviews)', type: 'checkbox-grid' as FieldType,
            value: ['push', 'email', 'whatsapp', 'sms'], // All selected by default
            options: [
                { value: 'push', label: 'Push' },
                { value: 'email', label: 'Email', disabled: true },
                { value: 'whatsapp', label: 'WhatsApp' },
                { value: 'sms', label: 'SMS' }
            ],
            helperText: 'Email notifications for bookings, changes, and cancellations cannot be unsubscribed as they are critical to user account management.'
        },
    ],
    edit: [
        { name: 'personalHeading', label: 'Personal Information', type: 'section-heading' as FieldType, colSpan: 'full' },
        { name: 'firstName', label: 'First Name', type: 'text' as FieldType, colSpan: 'half', placeholder: 'John' },
        { name: 'lastName', label: 'Last Name', type: 'text' as FieldType, colSpan: 'half', placeholder: 'Smith' },

        { name: 'contactHeading', label: 'Contact Information', type: 'section-heading' as FieldType, colSpan: 'full' },
        { name: 'email', label: 'Email Address', type: 'masked-revealer' as FieldType, colSpan: 'half', value: '' }, // Value hydrated by helper
        { name: 'phone', label: 'Phone Number', type: 'masked-revealer' as FieldType, colSpan: 'half', value: '', isMono: true },
        { name: 'dob', label: 'Date of Birth', type: 'date' as FieldType, colSpan: 'half', helperText: 'Customer must be at least 18 years old' },

        { name: 'securityHeading', label: 'Account Security', type: 'section-heading' as FieldType, colSpan: 'full' },
        {
            name: 'status',
            label: 'Account Status',
            type: 'radio-group' as FieldType,
            options: [
                { value: 'active', label: 'Active', badgeClass: 'bg-green-100 text-green-800' },
                { value: 'inactive', label: 'Inactive', badgeClass: 'bg-red-100 text-red-800' }
            ],
            helperText: "Inactive customers won't receive notifications and can't make new bookings"
        },

        { name: 'loyaltyHeading', label: 'Loyalty & Preferences', type: 'section-heading' as FieldType, colSpan: 'full' },
        {
            name: 'loyaltyTier', label: 'Loyalty Tier', type: 'select' as FieldType, colSpan: 'half', value: 'SELECT', options: [
                { value: 'SELECT', label: 'SELECT' },
                { value: 'SIGNATURE', label: 'SIGNATURE' },
                { value: 'ELITE', label: 'ELITE' },
                { value: 'BLACK', label: 'BLACK' },
                { value: 'PRIVÉ', label: 'PRIVÉ' }
            ]
        },
        { name: 'currency', label: 'Currency', type: 'select' as FieldType, colSpan: 'half', options: SHARED_CURRENCIES },

        { name: 'commHeading', label: 'Communication Preferences', type: 'section-heading' as FieldType, colSpan: 'full' },
        {
            name: 'marketingPrefs', label: 'Offers, Marketing, Surveys & Updates', type: 'checkbox-grid' as FieldType,
            options: [
                { value: 'push', label: 'Push' },
                { value: 'email', label: 'Email' },
                { value: 'whatsapp', label: 'WhatsApp' },
                { value: 'sms', label: 'SMS' }
            ]
        },
        {
            name: 'accountPrefs', label: 'Account Related (Bookings, Changes, Cancellations & Reviews)', type: 'checkbox-grid' as FieldType,
            helperText: 'Email notifications for bookings, changes, and cancellations cannot be unsubscribed.',
            options: [
                { value: 'push', label: 'Push' },
                { value: 'email', label: 'Email', disabled: true, locked: true },
                { value: 'whatsapp', label: 'WhatsApp' },
                { value: 'sms', label: 'SMS' }
            ]
        }
    ],
    view: [
        { name: 'section_1', label: 'Basic Information', type: 'section-heading' },
        { name: 'firstName', label: 'First Name', type: 'display-only', colSpan: 'half' },
        { name: 'lastName', label: 'Last Name', type: 'display-only', colSpan: 'half' },
        { name: 'dob', label: 'Date of Birth', type: 'display-only', colSpan: 'half' },
        { name: 'email', label: 'Email Address', type: 'masked-revealer', colSpan: 'half' },
        { name: 'phone', label: 'Phone Number', type: 'masked-revealer', colSpan: 'half', isMono: true },
        { name: 'status', label: 'Customer Status', type: 'status-badge', colSpan: 'half' },
        { name: 'joinDate', label: 'Join Date', type: 'display-only', colSpan: 'half', value: '1/15/2022' },
        { name: 'currency', label: 'Currency', type: 'display-only', colSpan: 'half' },
        { name: 'signupMethod', label: 'Signed Up Via', type: 'signup-badge', colSpan: 'half' },

        { name: 'section_2', label: 'Communication Preferences', type: 'section-heading' },
        {
            name: 'marketingPrefs',
            label: 'Offers, Marketing, Surveys & Updates',
            type: 'preference-icons',
            helperText: ''
        },
        {
            name: 'accountPrefs',
            label: 'Account Related (Bookings, Changes, Cancellations & Reviews)',
            type: 'preference-icons',
            helperText: 'Email notifications for bookings, changes, and cancellations cannot be unsubscribed as they are critical to user account management.',
            options: [
                { value: 'push', label: 'Push' },
                { value: 'email', label: 'Email', locked: true },
                { value: 'whatsapp', label: 'WhatsApp' },
                { value: 'sms', label: 'SMS' }
            ]
        },

        { name: 'section_3', label: 'Loyalty Information', type: 'section-heading' },
        { name: 'loyaltyTier', label: 'Loyalty Tier', type: 'status-badge', colSpan: 'half' },
        { name: 'balance', label: 'Loyalty Credits Balance', type: 'display-only', icon: 'ri-coin-line', colSpan: 'half' }
    ]
};
